import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { VisualInspectionFormDataState } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { usePublishRevisionHandler } from 'pages/reporting/shared/hooks/usePublishRevisionHandler'
import { useSignoffHandler } from 'pages/reporting/shared/hooks/useSignoffHandler'
import { useCallback, useState } from 'react'
import { GeckoVisualInspection, SignatureData } from 'types'

interface UseReportSubmissionProps {
  metadataEntryId: string
  formData: VisualInspectionFormDataState
  updateFormData: (data: Partial<VisualInspectionFormDataState>) => void
  selectedVisualInspection: GeckoVisualInspection | undefined
  currentRevisionNumber: number
  publishLatestDraftPDF: (pdfPath: string) => Promise<void>
  publishRevision: () => Promise<string>
}

interface UseReportSubmissionResult {
  isSubmitting: boolean
  handleSubmitReport: () => Promise<void>
  handleSignoff: (signatureData: SignatureData) => Promise<void>
  handlePublishRevision: (
    dataToUse?: VisualInspectionFormDataState
  ) => Promise<void>
}

export const useReportSubmission = ({
  metadataEntryId,
  formData,
  updateFormData,
  selectedVisualInspection,
  currentRevisionNumber,
  publishLatestDraftPDF,
  publishRevision,
}: UseReportSubmissionProps): UseReportSubmissionResult => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { rolodexPatch } = useRolodexEdit()

  const handleSubmitReport = useCallback(async () => {
    if (!metadataEntryId) {
      message.error('Metadata entry not yet created')
      return
    }

    try {
      setIsSubmitting(true)

      // Prepare complete report data
      const reportData = {
        mainInfo: formData.mainInfo,
        coverSectionData: formData.coverSectionData,
        checklistFindingsData: formData.checklistFindingsData,
        assetCorrosionAnalysisData: formData.assetCorrosionAnalysisData,
        findingsData: formData.findingsData,
        recommendationsData: formData.recommendationsData,
        pressureTestSubInspectionsData: formData.pressureTestSubInspectionsData,
        bundleSubInspectionsData: formData.bundleSubInspectionsData,
        inspectionTeamMemberData: formData.inspectionTeamMemberData,
        referenceDocumentsData: formData.referenceDocumentsData,
      }

      // Update Rolodex entry
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: reportData,
        },
      ])

      message.success('Report data submitted successfully!')
    } catch (error) {
      console.error('Failed to submit report:', error)
      message.error('Failed to submit report')
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }, [metadataEntryId, formData, rolodexPatch])

  const generatePdfPath = useCallback(
    (timestamp: string) => {
      return selectedVisualInspection
        ? `reportPDF/${selectedVisualInspection.id}-visual-report-r${currentRevisionNumber}-${timestamp}.pdf`
        : `reportPDF/visual-report-r${currentRevisionNumber}-${timestamp}.pdf`
    },
    [selectedVisualInspection, currentRevisionNumber]
  )

  const { handlePublishRevision } = usePublishRevisionHandler({
    metadataEntryId,
    formData,
    currentRevisionNumber,
    publishLatestDraftPDF,
    publishRevision,
    setIsSubmitting,
    generatePdfPath,
  })

  const { handleSignoff } = useSignoffHandler({
    metadataEntryId,
    formData,
    updateFormData,
    setIsSubmitting,
    handlePublishRevision,
  })

  return {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  }
}
